import type { CellContext } from "@tanstack/react-table";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { WORKFLOW_STATUS, WORKFLOW_TYPE } from "../../constants";
import { useWorkflowsData } from "../../context/WorkflowsDataProvider";
import { CommunityMemberColumn } from "../CommunityMemberColumn";
import { ActionGroup } from "../NewWorkflowV2/ActionGroup";
import { Actions } from "../Table/Actions";
import { Datetime } from "../Table/Datetime";
import { ScheduleType } from "../Table/ScheduleType";
import { WorkflowName } from "../Table/WorkflowName";
import { WorkflowsTable } from "../Table/WorkflowsTable";
import { NextRunAtColumn } from "./ScheduledColumns/NextRunAtColumn";
import { columnStyles } from "./columnStyles";

const i18nRoot = "settings.workflows.table";

const columns = ({ refetch }: { refetch: () => Promise<any> }) => [
  {
    accessorKey: "name",
    header: t([i18nRoot, "name"]),
    thClassName: columnStyles.title,
    cellClassName: columnStyles.title,
    cell: (info: CellContext<any, any>) => (
      <WorkflowName>
        {info.getValue() || t([i18nRoot, "untitled_workflow"])}
      </WorkflowName>
    ),
  },
  {
    accessorKey: "steps",
    header: t([i18nRoot, "steps"]),
    thClassName: columnStyles.steps,
    cellClassName: classNames("!py-0", columnStyles.steps),
    cell: (info: CellContext<any, any>) => (
      <ActionGroup
        actions={info.getValue()}
        size="md"
        truncationLimit={3}
        className="flex-wrap py-4"
      />
    ),
  },
  {
    accessorKey: "schedule_type",
    header: t([i18nRoot, "schedule_type"]),
    thClassName: columnStyles.scheduleType,
    cellClassName: columnStyles.scheduleType,
    cell: (info: CellContext<any, any>) => (
      <ScheduleType scheduleType={info.getValue()} />
    ),
  },
  {
    accessorKey: "created_by_community_member_id",
    header: t([i18nRoot, "created_by"]),
    thClassName: columnStyles.user,
    cellClassName: columnStyles.user,
    cell: (info: CellContext<any, any>) => (
      <CommunityMemberColumn id={info.getValue()} isLastNameHidden />
    ),
  },
  {
    accessorKey: "created_at",
    header: t([i18nRoot, "created_on"]),
    thClassName: columnStyles.dateShort,
    cellClassName: columnStyles.dateShort,
    cell: (info: CellContext<any, any>) => (
      <Datetime date={info.getValue()} shouldIncludeTime={false} />
    ),
  },
  {
    accessorKey: "next_run_at",
    header: t([i18nRoot, "next_run"]),
    thClassName: columnStyles.dateLong,
    cellClassName: columnStyles.dateLong,
    cell: (info: CellContext<any, any>) => (
      <NextRunAtColumn date={info.getValue()} steps={info.row.original.steps} />
    ),
  },
  {
    accessorKey: "last_run_at",
    header: t([i18nRoot, "last_run"]),
    thClassName: columnStyles.dateLong,
    cellClassName: columnStyles.dateLong,
    cell: (info: CellContext<any, any>) => (
      <Datetime date={info.getValue()} shouldIncludeTime />
    ),
  },
  {
    accessorKey: "tasks_count",
    header: t([i18nRoot, "total_runs"]),
    thClassName: columnStyles.number,
    thInnerClassName: "justify-end",
    cellClassName: classNames("text-right", columnStyles.number),
    cell: (info: CellContext<any, any>) => (
      <span className="break-normal">{info.getValue() || 0}</span>
    ),
  },
  {
    accessorKey: "actions",
    header: "",
    cellClassName: "cursor-default",
    onClick: ({ event }: { event: React.MouseEvent }) =>
      event.stopPropagation(),
    cell: ({ row }: CellContext<any, any>) => (
      <Actions workflow={row.original} refetch={refetch} />
    ),
  },
];

const getColumns =
  (shouldShowFewerColumns: boolean) =>
  ({ refetch }: { refetch: () => Promise<any> }) => {
    const allColumns = columns({ refetch });

    if (!shouldShowFewerColumns) {
      return allColumns;
    }

    const reducedColumnKeys = [
      "name",
      "schedule_type",
      "created_by_community_member_id",
    ];
    return allColumns.filter(column =>
      reducedColumnKeys.includes(column.accessorKey),
    );
  };

export const ScheduledTable = () => {
  const { category } = useWorkflowsData();

  return (
    <WorkflowsTable
      columns={getColumns(Boolean(category))}
      workflowType={WORKFLOW_TYPE.SCHEDULED}
      status={WORKFLOW_STATUS.ALL}
    />
  );
};
